import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { useDownloadImportTemplate } from 'modules/questionBank';

type TemplateFormat = 'xlsx' | 'csv' | 'txt' | 'json' | 'docx' | 'pdf';

/** "Not sure about the format?": a template download, one worked example per question type. */
export const ImportTemplateCard: React.FC = () => {
  const { t } = useTranslation();
  const downloadTemplate = useDownloadImportTemplate();
  const [format, setFormat] = useState<TemplateFormat>('xlsx');
  const label = `Download ${format.toUpperCase()} template`;

  return (
    <div className="flex h-full flex-col gap-2.5 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <FileSpreadsheet className="h-[18px] w-[18px]" />
        </span>
        <p className="text-sm font-semibold leading-snug">{t('import_template_title')}</p>
      </div>
      <p className="text-xs text-muted-foreground">{t('import_template_desc')}</p>
      <div className="mt-auto flex items-center gap-2 pt-1">
        <Select value={format} onValueChange={(value) => setFormat(value as TemplateFormat)}>
          <SelectTrigger id="template-format-select" className="min-w-0 flex-1 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
            <SelectItem value="csv">CSV (.csv)</SelectItem>
            <SelectItem value="txt">Plain Text (.txt)</SelectItem>
            <SelectItem value="json">JSON (.json)</SelectItem>
            <SelectItem value="docx">Word Document (.docx)</SelectItem>
            <SelectItem value="pdf">PDF (.pdf)</SelectItem>
          </SelectContent>
        </Select>
        <Button
          id="template-download-btn"
          variant="outline"
          onClick={() => downloadTemplate.mutate(format)}
          disabled={downloadTemplate.isPending}
          title={label}
          aria-label={label}
          className="w-9 shrink-0 p-0"
        >
          {downloadTemplate.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default ImportTemplateCard;
